// Libraries
import classNames from 'classnames';
import ReactSelect, {
	components,
	GroupBase,
	OptionProps,
	ValueContainerProps,
} from 'react-select';
import {Control, Controller} from 'react-hook-form';
import styles from './FormSelect.module.scss';
import {getFieldValueByDynamicKey} from '@shared/helpers/objectHelper';
import {useMemo} from 'react';
import {MenuPortalProps} from 'react-select/dist/declarations/src/components/Menu';
import {useTranslation} from 'react-i18next';

export type SelectOption = {label: string; value: string | boolean | number};
export type GroupedOption = GroupBase<SelectOption>;
type SelectValue = SelectOption | SelectOption[] | null | undefined;

interface FormSelectProps {
	name: string;
	options: SelectOption[] | GroupedOption[];
	label?: string;
	classes?: string;
	control: Control<any>;
	isMulti: boolean;
	menuPortal: boolean;
	selectClasses?: string;
	closeMenuOnSelect: boolean;
	values?: {label: string; value: string}[] | {label: string; value: string};
	disabledOptions?: string[];
	onBlur?: () => void;
	onChange?: (newVal: any) => void;
	disabled?: boolean;
}

interface ControlledFormSelectProps {
	options: SelectOption[] | GroupedOption[];
	value?: SelectValue;
	isMulti?: boolean;
	menuPortal?: boolean;
	selectClasses?: string;
	closeMenuOnSelect?: boolean;
	disabledOptions?: string[];
	onBlur?: () => void;
	onChange: (newValue: SelectOption | SelectOption[] | null) => void;
	disabled?: boolean;
	placeholder?: string;
	hasError?: boolean;
	ariaLabel?: string;
	defaultValue?: SelectValue;
	selectRef?: any;
}

const MenuPortal = (props: MenuPortalProps<any, boolean, GroupBase<any>>) => {
	return (
		<components.MenuPortal {...props}>
			<div className={styles.portal}>{props.children}</div>
		</components.MenuPortal>
	);
};

const ValueComponent = (
	props: ValueContainerProps<any, boolean, GroupBase<any>>,
) => {
	if (Array.isArray((props.children as any)[0])) {
		if (!!props.selectProps.menuPortalTarget) {
			return (
				<div className={styles.containerWrapper}>
					{`(${(props.children as any)[0].length})`}
					{props.children}
				</div>
			);
		} else {
			const value = Array.isArray(props.selectProps.value)
				? (props.selectProps.value as any).map((x: any) => x.label).join(', ')
				: (props.selectProps.value as any).label;

			return (
				<div className={styles.containerWrapper}>
					{value}
					{props.children}
				</div>
			);
		}
	}
	return <div className={styles.containerWrapper}>{props.children}</div>;
};

const OptionComponent = (props: OptionProps<any, boolean, GroupBase<any>>) => {
	return (
		<div
			data-current={props.isSelected}
			className={classNames({
				[styles.option]: !props.isMulti,
				[styles.multiOption]: !!props.isMulti,
				[styles.disabledOption]: props.isDisabled,
			})}
			key={`${props.data.label}`}
			onChange={() => null}
			onClick={() => !props.isDisabled && props.selectOption(props.data)}>
			<span>{props.data.label}</span>
		</div>
	);
};

const selectStyles = {
	menu: (base: any) => ({
		...base,
		zIndex: 999999,
	}),
	menuPortal: (base: any) => ({
		...base,
		zIndex: 999999,
	}),
};

export const flattenSelectOptions = (
	options: (SelectOption | GroupedOption)[],
): SelectOption[] => {
	return options.flatMap((opt: any) => (opt.options ? opt.options : [opt]));
};

export const ControlledFormSelect = (props: ControlledFormSelectProps) => {
	const {
		options,
		value,
		isMulti = false,
		menuPortal = true,
		selectClasses,
		closeMenuOnSelect = false,
		disabledOptions,
		onBlur,
		onChange,
		disabled,
		placeholder = '',
		hasError,
		ariaLabel,
		defaultValue,
		selectRef,
	} = props;

	const isOptionDisabled = (option: SelectOption): boolean => {
		return typeof option.value !== 'boolean'
			? (disabledOptions?.includes(option.value.toString()) ?? false)
			: false;
	};

	return (
		<ReactSelect<SelectOption, boolean, GroupBase<SelectOption>>
			aria-label={ariaLabel}
			ref={selectRef}
			isDisabled={disabled}
			isSearchable={false}
			isMulti={isMulti}
			options={options}
			openMenuOnClick={true}
			hideSelectedOptions={false}
			placeholder={placeholder}
			isClearable={false}
			isOptionDisabled={isOptionDisabled}
			menuPortalTarget={menuPortal && typeof document !== 'undefined' ? document.body : undefined}
			menuPosition={menuPortal ? 'fixed' : 'absolute'}
			styles={selectStyles}
			closeMenuOnSelect={closeMenuOnSelect}
			className={classNames('select', selectClasses, {
				[styles.error]: !!hasError,
			})}
			defaultValue={defaultValue}
			value={value ?? null}
			getOptionLabel={(option: any) => option?.label}
			onChange={(newValue) => onChange(newValue as SelectOption | SelectOption[] | null)}
			onMenuClose={onBlur}
			components={{
				ValueContainer: ValueComponent,
				Option: OptionComponent,
				Group: components.Group,
				MenuPortal: MenuPortal,
			}}
		/>
	);
};

const FormSelect = (props: FormSelectProps) => {
	const {
		options,
		classes,
		label,
		control,
		closeMenuOnSelect,
		name,
		values,
		isMulti,
		menuPortal,
		selectClasses,
		disabledOptions,
		onBlur,
		onChange,
		disabled,
	} = props;
	const {t} = useTranslation();
	const flatOptions = useMemo(() => flattenSelectOptions(options), [options]);

	return (
		<div className={classNames('input select', classes)}>
			<label className='input__label'>
				{label && <span className='input__label-text'>{label}</span>}
				<Controller
					control={control}
					name={name}
					render={(e: any) => {
						const errors = e.formState.errors;
						let error = {message: null};
						if (Object.keys(errors).length > 0) {
							error = getFieldValueByDynamicKey(errors, name);
						}
						return (
							<ControlledFormSelect
								options={options}
								ariaLabel={label}
								defaultValue={values}
								selectRef={e.field.ref}
								placeholder={t('common.select') + '...'}
								disabled={disabled}
								isMulti={isMulti}
								menuPortal={menuPortal}
								disabledOptions={disabledOptions}
								closeMenuOnSelect={closeMenuOnSelect}
								selectClasses={selectClasses}
								hasError={!!error?.message}
								value={
									Array.isArray(e.field.value)
										? flatOptions.filter((x) => e.field.value.includes(x.value))
										: flatOptions.find((x) => x.value === e.field.value) ?? values
								}
								onChange={(new_value: any) => {
									if (Array.isArray(new_value)) {
										e.field.onChange(new_value.map((x) => x.value));
										return;
									}

									if (!!onChange) onChange(new_value.value);
									e.field.onChange(new_value.value);
								}}
								onBlur={onBlur}
							/>
						);
					}}
				/>
			</label>
		</div>
	);
};

FormSelect.defaultProps = {
	isMulti: false,
	menuPortal: true,
	closeMenuOnSelect: false,
};

export default FormSelect;
